/* eslint-disable */
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Typography,
  Grid,
  Chip,
  IconButton,
  Paper,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import CloseIcon from "@material-ui/icons/Close";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import PersonIcon from "@material-ui/icons/Person";
import PhoneIcon from "@material-ui/icons/Phone";
import PaymentIcon from "@material-ui/icons/Payment";
import BuildIcon from "@material-ui/icons/Build";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import InfoIcon from "@material-ui/icons/Info";

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  dialogActions: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  label: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    "& svg": {
      marginRight: theme.spacing(1),
      fontSize: 18,
      color: theme.palette.primary.main,
    },
  },
  value: {
    fontWeight: 400,
  },
  gridLine: {
    marginBottom: theme.spacing(1.5),
  },
  statusChip: {
    fontSize: "0.8rem",
    height: 24,
  },
  titleText: {
    fontWeight: 500,
  },
  whatsappButton: {
    backgroundColor: "#25D366",
    color: "white",
    "&:hover": {
      backgroundColor: "#128C7E",
    },
  },
  sectionTitle: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  infoContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
}));

/**
 * A component that renders a labeled grid line with icon.
 *
 * @param {object} props - The component props.
 * @param {string} props.label - The label to display on the grid line.
 * @param {string} props.value - The value to display associated with the label.
 * @param {React.ReactNode} props.icon - The icon to display next to the label.
 *
 * @returns {React.ReactElement} The rendered grid line component.
 */
function GridLine({ label, value, icon }) {
  const classes = useStyles();

  return (
    <Grid item container xs={12} className={classes.gridLine}>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle2" className={classes.label}>
          {icon}
          {label}
        </Typography>
      </Grid>
      <Grid item xs={12} md={8}>
        <Typography variant="body2" className={classes.value}>
          {value}
        </Typography>
      </Grid>
    </Grid>
  );
}

/**
 * A dialog component that displays information about a customer.
 *
 * @param {object} props - The component props.
 * @param {object} props.customerData - The customer data object.
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {Function} props.toggleDialog - Function to toggle the dialog open state.
 *
 * @returns {React.ReactElement} The rendered dialog component.
 */
function AgentReportDialog({
  customerData = {},
  open = false,
  toggleDialog,
  impersonation,
}) {
  const classes = useStyles();

  const getStatusColor = status => {
    if (!status) return "default";

    const statusLower = status.toLowerCase();
    if (
      statusLower.includes("active") ||
      statusLower.includes("complete") ||
      statusLower.includes("paid")
    ) {
      return "primary";
    } else if (
      statusLower.includes("pending") ||
      statusLower.includes("progress")
    ) {
      return "secondary";
    } else if (statusLower.includes("cancel") || statusLower.includes("fail")) {
      return "error";
    }
    return "default";
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6" className={classes.titleText}>
          {customerData?.orders_import_id
            ? `Order: ${customerData.orders_import_id}`
            : "Order Details"}
        </Typography>
        <IconButton
          onClick={toggleDialog}
          size="small"
          aria-label="close"
          style={{ color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper variant="outlined" className={classes.infoContainer}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Customer Information
              </Typography>

              {customerData.customer_name && (
                <GridLine
                  label="Contact Name"
                  value={customerData.customer_name}
                  icon={<PersonIcon />}
                />
              )}

              {customerData.customer_address && (
                <GridLine
                  label="Address"
                  value={customerData.customer_address}
                  icon={<LocationOnIcon />}
                />
              )}

              {customerData?.customer_phone?.length === 9 && (
                <GridLine
                  label="Contact Number"
                  value={`0${customerData.customer_phone}`}
                  icon={<PhoneIcon />}
                />
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper variant="outlined" className={classes.infoContainer}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Order Timeline
              </Typography>

              {customerData.order_created_date && (
                <GridLine
                  label="Created Date"
                  value={customerData.order_created_date}
                  icon={<CalendarTodayIcon />}
                />
              )}

              {customerData.order_paid_date && (
                <GridLine
                  label="Payment Date"
                  value={customerData.order_paid_date}
                  icon={<PaymentIcon />}
                />
              )}

              {customerData.order_installation_date && (
                <GridLine
                  label="Installation Date"
                  value={customerData.order_installation_date}
                  icon={<BuildIcon />}
                />
              )}

              {customerData.order_activation_date_2 && (
                <GridLine
                  label="Activation Date"
                  value={customerData.order_activation_date}
                  icon={<CheckCircleIcon />}
                />
              )}

              {customerData.paid_date && (
                <GridLine
                  label="Commission Paid"
                  value={customerData.paid_date}
                  icon={<PaymentIcon />}
                />
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper variant="outlined" className={classes.infoContainer}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Status Information
              </Typography>

              {customerData.customer_status_2 && (
                <GridLine
                  label="Activation Status"
                  value={
                    <Chip
                      label={customerData.customer_status_2}
                      size="small"
                      color={getStatusColor(customerData.customer_status_2)}
                      className={classes.statusChip}
                    />
                  }
                  icon={<InfoIcon />}
                />
              )}

              {customerData.customer_status_1 && (
                <GridLine
                  label="Current Status"
                  value={
                    <Chip
                      label={customerData.customer_status_1}
                      size="small"
                      color={getStatusColor(customerData.customer_status_1)}
                      className={classes.statusChip}
                    />
                  }
                  icon={<InfoIcon />}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        {customerData?.customer_phone?.length === 9 && (
          <Tooltip title={`WhatsApp 0${customerData.customer_phone}`}>
            <Button
              disabled={impersonation}
              variant="contained"
              className={classes.whatsappButton}
              startIcon={<WhatsAppIcon />}
              onClick={() => {
                window.open(
                  `https://wa.me/+27${customerData.customer_phone}`,
                  "_blank"
                );
              }}
            >
              WhatsApp Customer
            </Button>
          </Tooltip>
        )}
        <Button variant="outlined" color="primary" onClick={toggleDialog}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AgentReportDialog;
